import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Table, Tag, Button, Space, Spin, Alert, Typography, Badge, message } from 'antd';
import { ReloadOutlined, DownloadOutlined, CopyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getPanelVinculacionIncoherencias,
  crmClienteUrl,
  crmFarmaciaUrl,
  type PanelIncoherencias,
  type Inc01Row,
  type Inc02Row,
  type Inc04Row,
  type Inc05Row,
  type Inc06Row,
  type Inc07Row,
  type Inc08Row,
  type Inc09Row,
  type BiunivocidadSubtipo,
} from '../../api/vinculacionApi';

const { Title, Text, Paragraph } = Typography;

// ── Helpers ─────────────────────────────────────────────────────────
const nf = new Intl.NumberFormat('es-ES');
const pctStr = (x: number) => `${Math.round(x * 100)}%`;

const CrmCliente: React.FC<{ id?: number; children: React.ReactNode }> = ({ id, children }) =>
  id ? (
    <a href={crmClienteUrl(id)} target="_blank" rel="noreferrer" title="Abrir ficha en CRM">
      {children}
    </a>
  ) : (
    <>{children}</>
  );

const CrmFarmacia: React.FC<{ id?: number; children: React.ReactNode }> = ({ id, children }) =>
  id ? (
    <a href={crmFarmaciaUrl(id)} target="_blank" rel="noreferrer" title="Abrir ficha de farmacia en CRM">
      {children}
    </a>
  ) : (
    <>{children}</>
  );

const num = (a?: number, b?: number) => (a ?? 0) - (b ?? 0);
const txt = (a?: string, b?: string) => (a ?? '').localeCompare(b ?? '');

/** Descarga un CSV (separador ';', BOM para Excel). */
const exportCsv = (filename: string, headers: string[], rows: (string | number)[][]) => {
  const esc = (v: string | number) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [headers.map(esc).join(';'), ...rows.map((r) => r.map(esc).join(';'))];
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

/** Botón-filtro con contador (estilo GCvsc). */
const FilterBtn: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active,
  onClick,
  children,
}) => (
  <Button size="small" type={active ? 'primary' : 'default'} onClick={onClick}>
    {children}
  </Button>
);

// ── Metadatos de cada incoherencia (título + descripción) ───────────
// Listados ordenados: primero farmacias, luego farmacéuticos. `key` estable e independiente del orden.
interface ListadoDef {
  key: string;
  titulo: string;
  descripcion: string;
}
const LISTADOS: ListadoDef[] = [
  {
    key: 'far_sin_titular_gs',
    titulo: 'Farmacias sin titular en Gescom',
    descripcion:
      'Farmacias activas en Gescom-AQ que no tienen titular activo. Al aplicar la regla «un farmacéutico no puede ser titular de dos farmacias» en la reconciliación la farmacia puede quedar sin titular.',
  },
  {
    key: 'far_sin_titular_cu2',
    titulo: 'Farmacias sin titular en CU2',
    descripcion: 'Farmacias en CU2 que no tienen ningún titular (propietario) registrado.',
  },
  {
    key: 'far_no_biunivoca',
    titulo: 'Farmacias — vinculación no biunívoca',
    descripcion:
      'Farmacias CU2 (X → id_farmacia_gs = Y) donde Gescom-AQ[Y].[3_id_farmacia_CU2] ≠ X: rompe la reciprocidad de la vinculación.',
  },
  {
    key: 'far_baja_fc',
    titulo: 'Farmacias vinculadas — baja o familia incorrecta',
    descripcion:
      'Entidades Gescom-AQ dadas de baja o con _FamilyClass ≠ 15 que siguen vinculadas a CU2 (3_id_farmacia_CU2 no nulo).',
  },
  {
    key: 'fco_titular_empleado',
    titulo: 'Farmacéuticos Gescom — titular y empleado a la vez',
    descripcion:
      'Clientes Gescom-AQ que figuran como titular activo de una farmacia y empleado activo de otra (o la misma) simultáneamente.',
  },
  {
    key: 'fco_no_biunivoca',
    titulo: 'Farmacéuticos — vinculación no biunívoca',
    descripcion:
      'Colegiados CU2 (X → id_colegiado_gs = Y) donde Gescom-AQ[Y].id_colegiado_CU2 ≠ X: rompe la reciprocidad de la vinculación.',
  },
  {
    key: 'col_repetida',
    titulo: 'Colegiados CU2 con vinculación repetida (doble colegiación o error)',
    descripcion:
      'Grupos de colegiados CU2 que comparten el mismo id_colegiado_gs (doble colegiación u otros motivos).',
  },
  {
    key: 'fco_baja_fc',
    titulo: 'Farmacéuticos vinculados — baja o familia incorrecta',
    descripcion:
      'Clientes Gescom-AQ dados de baja o con _FamilyClass ≠ 59 que siguen vinculados a CU2 (id_colegiado_CU2 no nulo).',
  },
];

// Subtipo de rotura de biunivocidad → etiqueta + color
const SUBTIPO_META: Record<BiunivocidadSubtipo, { label: string; color: string }> = {
  SIN_BACKLINK: { label: 'Sin back-link', color: 'gold' },
  OTRO_CU2: { label: 'Apunta a otro CU2', color: 'red' },
  ID_INVALIDO: { label: 'id_gs inválido', color: 'volcano' },
};
const subtipoTag = (s: BiunivocidadSubtipo) => <Tag color={SUBTIPO_META[s].color}>{SUBTIPO_META[s].label}</Tag>;

const rolTag = (propietario: boolean) =>
  propietario ? <Tag color="blue">Titular</Tag> : <Tag color="purple">Empleado</Tag>;

const vincFarmaciaTag = (vinc: boolean) =>
  vinc ? <Tag color="green">Vinculada</Tag> : <Tag>Sin vincular</Tag>;

// ── Tablas por INC ──────────────────────────────────────────────────

const Inc01Table: React.FC<{ rows: Inc01Row[] }> = ({ rows }) => {
  const columns: ColumnsType<Inc01Row> = [
    { title: 'ID', dataIndex: 'id', align: 'right', sorter: (a, b) => num(a.id, b.id) },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      sorter: (a, b) => txt(a.nombre, b.nombre),
      render: (_, r) => <CrmCliente id={r.id}>{r.nombre}</CrmCliente>,
    },
    {
      title: 'Motivo',
      key: 'motivo',
      render: (_, r) => {
        const m: string[] = [];
        if (r.familyClass !== 59) m.push(`FC=${r.familyClass}`);
        if (r.fBaja) m.push(`Baja=${r.fBaja}`);
        return m.join(', ');
      },
    },
    {
      title: 'Rol',
      key: 'rol',
      render: (_, r) => (
        <>
          {r.esTitular && <Tag color="blue">Titular</Tag>}
          {r.esEmpleado && <Tag color="purple">Empleado</Tag>}
          {!r.esTitular && !r.esEmpleado && '-'}
        </>
      ),
    },
    { title: 'Vinculado', key: 'vinc', render: (_, r) => (r.idColegiadoCU2 ? <Tag color="green">Vinculado</Tag> : null) },
    { title: 'Nombre CU2', dataIndex: 'nombreColegiadoCU2' },
    { title: 'id_colegiado_CU2', dataIndex: 'idColegiadoCU2', align: 'right' },
  ];
  return <Table rowKey="id" size="small" columns={columns} dataSource={rows} scroll={{ x: true }} pagination={{ pageSize: 50, showSizeChanger: true }} />;
};

const Inc02Table: React.FC<{ rows: Inc02Row[] }> = ({ rows }) => {
  const columns: ColumnsType<Inc02Row> = [
    { title: 'ID', dataIndex: 'id', align: 'right', sorter: (a, b) => num(a.id, b.id) },
    { title: 'Nombre', dataIndex: 'nombre', sorter: (a, b) => txt(a.nombre, b.nombre), render: (_, r) => <CrmCliente id={r.id}>{r.nombre}</CrmCliente> },
    { title: 'Farmacia (titular)', dataIndex: 'farmaciaTitular', align: 'right' },
    { title: 'Farmacia (empleado)', dataIndex: 'farmaciaEmpleado', align: 'right' },
    { title: 'id_colegiado_CU2', dataIndex: 'idColegiadoCU2', align: 'right' },
  ];
  return <Table rowKey="id" size="small" columns={columns} dataSource={rows} scroll={{ x: true }} pagination={{ pageSize: 50, showSizeChanger: true }} />;
};

const CONF_COLOR: Record<string, string> = { SEGURO: 'green', DUDOSO: 'purple', ERROR: 'red' };

const Inc04Table: React.FC<{ rows: Inc04Row[] }> = ({ rows }) => {
  const [selected, setSelected] = useState<React.Key[]>([]);
  // Marca de inicio de grupo (cambia id_colegiado_gs respecto a la fila anterior)
  const groupStart = useMemo(() => {
    const s = new Set<number>();
    let prev: number | null = null;
    rows.forEach((r) => {
      if (prev !== null && r.idColegiadoGs !== prev) s.add(r.idColegiado);
      prev = r.idColegiadoGs;
    });
    return s;
  }, [rows]);

  const columns: ColumnsType<Inc04Row> = [
    { title: 'id_colegiado_gs', dataIndex: 'idColegiadoGs', align: 'right', sorter: (a, b) => num(a.idColegiadoGs, b.idColegiadoGs) },
    { title: 'Nombre Gescom', key: 'gescom', render: (_, r) => <CrmCliente id={r.idColegiadoGs}>{r.nombreGescom}</CrmCliente> },
    { title: 'id_colegiado CU2', dataIndex: 'idColegiado', align: 'right', sorter: (a, b) => num(a.idColegiado, b.idColegiado) },
    { title: 'Nombre CU2', dataIndex: 'nombreColegiado', sorter: (a, b) => txt(a.nombreColegiado, b.nombreColegiado) },
    { title: 'Rol', key: 'rol', render: (_, r) => rolTag(r.propietario) },
    { title: 'Num. col.', dataIndex: 'numColegiado', align: 'right' },
    { title: 'Colegio', dataIndex: 'colegio' },
  ];

  const copySelected = async () => {
    if (selected.length === 0) {
      message.info('Ningún colegiado seleccionado');
      return;
    }
    const texto = selected.join(',');
    try {
      await navigator.clipboard.writeText(texto);
      message.success(`${selected.length} id_colegiado copiados`);
    } catch {
      message.warning(texto);
    }
  };

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button size="small" icon={<CopyOutlined />} onClick={copySelected}>
          Copiar id_colegiado seleccionados
        </Button>
        <Text type="secondary">{selected.length > 0 ? `${selected.length} seleccionados` : ''}</Text>
      </Space>
      <Table
        rowKey="idColegiado"
        size="small"
        columns={columns}
        dataSource={rows}
        scroll={{ x: true }}
        pagination={{ pageSize: 50, showSizeChanger: true }}
        rowSelection={{ selectedRowKeys: selected, onChange: setSelected }}
        rowClassName={(r) => (groupStart.has(r.idColegiado) ? 'inc-group-start' : '')}
      />
    </>
  );
};

const Inc05Table: React.FC<{ rows: Inc05Row[] }> = ({ rows }) => {
  const [filtro, setFiltro] = useState<'' | 'vinc' | 'sinv' | 'ambos'>('');
  const counts = useMemo(
    () => ({
      vinc: rows.filter((r) => r.idFarmaciaCU2).length,
      sinv: rows.filter((r) => !r.idFarmaciaCU2).length,
      ambos: rows.filter((r) => r.idFarmaciaCU2 && r.cu2SinTitular).length,
    }),
    [rows],
  );
  const data =
    filtro === 'vinc'
      ? rows.filter((r) => r.idFarmaciaCU2)
      : filtro === 'sinv'
      ? rows.filter((r) => !r.idFarmaciaCU2)
      : filtro === 'ambos'
      ? rows.filter((r) => r.idFarmaciaCU2 && r.cu2SinTitular)
      : rows;

  const farmaciaTxt = (r: Inc05Row) => {
    const d = (r.direccion ?? '').trim();
    const p = (r.poblacion ?? '').trim();
    return d && p ? `${d} - ${p}` : d || p || (r.nombreGescom ?? '').trim() || '(ver ficha)';
  };
  const cu2Txt = (r: Inc05Row) => {
    const d = (r.cu2Direccion ?? '').trim();
    const l = (r.cu2Localidad ?? '').trim();
    return d && l ? `${d} - ${l}` : d || l;
  };

  const columns: ColumnsType<Inc05Row> = [
    { title: 'ID', dataIndex: 'idEntidad', align: 'right', sorter: (a, b) => num(a.idEntidad, b.idEntidad) },
    { title: 'Farmacia (Gescom-AQ)', key: 'farmacia', render: (_, r) => <CrmFarmacia id={r.idEntidad}>{farmaciaTxt(r)}</CrmFarmacia> },
    { title: 'Provincia', dataIndex: 'provincia', sorter: (a, b) => txt(a.provincia, b.provincia) },
    { title: 'Vinculación', key: 'vinc', render: (_, r) => vincFarmaciaTag(!!r.idFarmaciaCU2) },
    { title: 'id_farmacia_CU2', dataIndex: 'idFarmaciaCU2', align: 'right' },
    {
      title: 'Farmacia CU2',
      key: 'cu2',
      render: (_, r) => (
        <>
          {r.idFarmaciaCU2 && r.cu2SinTitular && (
            <span title="La farmacia CU2 tampoco tiene titular: sin titular en ambos sistemas" style={{ color: '#c62828', fontWeight: 700, marginRight: 4 }}>
              ⚠
            </span>
          )}
          {cu2Txt(r)}
        </>
      ),
    },
    { title: 'Nombre farmacia (CU2)', dataIndex: 'cu2NombreFarmacia' },
  ];

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <Text type="secondary">Filtrar:</Text>
        <FilterBtn active={filtro === ''} onClick={() => setFiltro('')}>Todas ({rows.length})</FilterBtn>
        <FilterBtn active={filtro === 'vinc'} onClick={() => setFiltro('vinc')}>Vinculadas {counts.vinc}</FilterBtn>
        <FilterBtn active={filtro === 'sinv'} onClick={() => setFiltro('sinv')}>Sin vincular {counts.sinv}</FilterBtn>
        <FilterBtn active={filtro === 'ambos'} onClick={() => setFiltro('ambos')}>⚠ Sin titular en ambos {counts.ambos}</FilterBtn>
      </Space>
      {counts.ambos > 0 && (
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          <span style={{ color: '#c62828', fontWeight: 700 }}>⚠</span> la contraparte CU2 tampoco tiene titular — sin titular en ambos
          sistemas (<b>{counts.ambos}</b> casos).
        </Paragraph>
      )}
      <Table rowKey="idEntidad" size="small" columns={columns} dataSource={data} scroll={{ x: true }} pagination={{ pageSize: 50, showSizeChanger: true }} />
    </>
  );
};

const Inc06Table: React.FC<{ rows: Inc06Row[] }> = ({ rows }) => {
  const [filtro, setFiltro] = useState<'' | 'vinc' | 'sinv'>('');
  const counts = useMemo(
    () => ({ vinc: rows.filter((r) => r.idFarmaciaGs).length, sinv: rows.filter((r) => !r.idFarmaciaGs).length }),
    [rows],
  );
  const data = filtro === 'vinc' ? rows.filter((r) => r.idFarmaciaGs) : filtro === 'sinv' ? rows.filter((r) => !r.idFarmaciaGs) : rows;

  const columns: ColumnsType<Inc06Row> = [
    { title: 'id_farmacia', dataIndex: 'idFarmacia', align: 'right', sorter: (a, b) => num(a.idFarmacia, b.idFarmacia) },
    { title: 'Nombre farmacia', dataIndex: 'nombreFarmacia', sorter: (a, b) => txt(a.nombreFarmacia, b.nombreFarmacia) },
    { title: 'Dirección', dataIndex: 'direccion' },
    { title: 'Localidad', dataIndex: 'localidad', sorter: (a, b) => txt(a.localidad, b.localidad) },
    { title: 'Provincia', dataIndex: 'provincia', sorter: (a, b) => txt(a.provincia, b.provincia) },
    { title: 'Vinculación', key: 'vinc', render: (_, r) => vincFarmaciaTag(!!r.idFarmaciaGs) },
    { title: 'id_farmacia_gs', dataIndex: 'idFarmaciaGs', align: 'right' },
    { title: 'Farmacia Gescom', key: 'gescom', render: (_, r) => (r.idFarmaciaGs ? <CrmFarmacia id={r.idFarmaciaGs}>{r.nombreGescom || '(ver ficha)'}</CrmFarmacia> : null) },
  ];

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <Text type="secondary">Filtrar:</Text>
        <FilterBtn active={filtro === ''} onClick={() => setFiltro('')}>Todas ({rows.length})</FilterBtn>
        <FilterBtn active={filtro === 'vinc'} onClick={() => setFiltro('vinc')}>Vinculadas {counts.vinc}</FilterBtn>
        <FilterBtn active={filtro === 'sinv'} onClick={() => setFiltro('sinv')}>Sin vincular {counts.sinv}</FilterBtn>
      </Space>
      <Table rowKey="idFarmacia" size="small" columns={columns} dataSource={data} scroll={{ x: true }} pagination={{ pageSize: 50, showSizeChanger: true }} />
    </>
  );
};

const SUBTIPOS: BiunivocidadSubtipo[] = ['SIN_BACKLINK', 'OTRO_CU2', 'ID_INVALIDO'];

const Inc07Table: React.FC<{ rows: Inc07Row[] }> = ({ rows }) => {
  const [filtro, setFiltro] = useState<'' | BiunivocidadSubtipo>('');
  const [ocultarDobles, setOcultarDobles] = useState(false);
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    SUBTIPOS.forEach((s) => (c[s] = rows.filter((r) => r.subtipo === s).length));
    return c;
  }, [rows]);
  const nDobles = useMemo(() => rows.filter((r) => r.dobleColegiacion).length, [rows]);
  let data = filtro ? rows.filter((r) => r.subtipo === filtro) : rows;
  if (ocultarDobles) data = data.filter((r) => !r.dobleColegiacion);

  const columns: ColumnsType<Inc07Row> = [
    { title: 'id_colegiado CU2', dataIndex: 'idColegiado', align: 'right', sorter: (a, b) => num(a.idColegiado, b.idColegiado) },
    { title: 'Nombre CU2', dataIndex: 'nombreColegiado', sorter: (a, b) => txt(a.nombreColegiado, b.nombreColegiado) },
    { title: 'id_colegiado_gs', dataIndex: 'idColegiadoGs', align: 'right', sorter: (a, b) => num(a.idColegiadoGs, b.idColegiadoGs) },
    {
      title: 'Subtipo',
      key: 'subtipo',
      render: (_, r) => (
        <Space size={4}>
          {subtipoTag(r.subtipo)}
          {r.dobleColegiacion && <Tag color="default">Doble colegiación</Tag>}
        </Space>
      ),
    },
    {
      title: 'Back-link actual',
      key: 'backlink',
      align: 'right',
      render: (_, r) => (r.backlinkActual != null ? r.backlinkActual : '—'),
    },
    { title: 'Gescom', key: 'gescom', render: (_, r) => (r.subtipo === 'ID_INVALIDO' ? '—' : <CrmCliente id={r.idColegiadoGs}>{r.nombreGescom || 'ver ficha'}</CrmCliente>) },
    {
      title: 'Confianza',
      key: 'confianza',
      sorter: (a, b) => num(a.score, b.score),
      render: (_, r) =>
        r.confianza ? (
          <>
            <Tag color={CONF_COLOR[r.confianza]}>{r.confianza}</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>{pctStr(r.score ?? 0)}</Text>
          </>
        ) : (
          '—'
        ),
    },
    { title: 'Rol', key: 'rol', render: (_, r) => rolTag(r.propietario) },
    { title: 'Num. col.', dataIndex: 'numColegiado', align: 'right' },
    { title: 'Colegio', dataIndex: 'colegio' },
  ];

  const doExport = () =>
    exportCsv(
      'INC-07_vinculacion_no_biunivoca_farmaceuticos.csv',
      ['id_colegiado_CU2', 'Nombre_CU2', 'id_colegiado_gs', 'Subtipo', 'Doble_colegiacion', 'Backlink_actual', 'Nombre_Gescom', 'Confianza', 'Score', 'Rol', 'Num_colegiado', 'Colegio'],
      data.map((r) => [
        r.idColegiado,
        r.nombreColegiado ?? '',
        r.idColegiadoGs,
        SUBTIPO_META[r.subtipo].label,
        r.dobleColegiacion ? 'Sí' : '',
        r.backlinkActual ?? '',
        r.nombreGescom ?? '',
        r.confianza ?? '',
        r.score != null ? pctStr(r.score) : '',
        r.propietario ? 'Titular' : 'Empleado',
        r.numColegiado ?? '',
        r.colegio ?? '',
      ]),
    );

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <Text type="secondary">Filtrar:</Text>
        <FilterBtn active={filtro === ''} onClick={() => setFiltro('')}>Todos ({rows.length})</FilterBtn>
        {SUBTIPOS.map((s) => (
          <FilterBtn key={s} active={filtro === s} onClick={() => setFiltro(s)}>
            {SUBTIPO_META[s].label} {counts[s]}
          </FilterBtn>
        ))}
        {nDobles > 0 && (
          <FilterBtn active={ocultarDobles} onClick={() => setOcultarDobles((v) => !v)}>
            {ocultarDobles ? 'Mostrar' : 'Ocultar'} dobles colegiaciones ({nDobles})
          </FilterBtn>
        )}
        <Button size="small" icon={<DownloadOutlined />} onClick={doExport}>Exportar CSV</Button>
      </Space>
      <Table rowKey="idColegiado" size="small" columns={columns} dataSource={data} scroll={{ x: true }} pagination={{ pageSize: 50, showSizeChanger: true }} />
    </>
  );
};

const Inc08Table: React.FC<{ rows: Inc08Row[] }> = ({ rows }) => {
  const [filtro, setFiltro] = useState<'' | BiunivocidadSubtipo>('');
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    SUBTIPOS.forEach((s) => (c[s] = rows.filter((r) => r.subtipo === s).length));
    return c;
  }, [rows]);
  const data = filtro ? rows.filter((r) => r.subtipo === filtro) : rows;

  const columns: ColumnsType<Inc08Row> = [
    { title: 'id_farmacia', dataIndex: 'idFarmacia', align: 'right', sorter: (a, b) => num(a.idFarmacia, b.idFarmacia) },
    { title: 'Nombre farmacia (CU2)', dataIndex: 'nombreFarmacia', sorter: (a, b) => txt(a.nombreFarmacia, b.nombreFarmacia) },
    { title: 'id_farmacia_gs', dataIndex: 'idFarmaciaGs', align: 'right', sorter: (a, b) => num(a.idFarmaciaGs, b.idFarmaciaGs) },
    { title: 'Subtipo', key: 'subtipo', render: (_, r) => subtipoTag(r.subtipo) },
    { title: 'Back-link actual', key: 'backlink', align: 'right', render: (_, r) => (r.backlinkActual != null ? r.backlinkActual : '—') },
    { title: 'Farmacia Gescom', key: 'gescom', render: (_, r) => (r.subtipo === 'ID_INVALIDO' ? '—' : <CrmFarmacia id={r.idFarmaciaGs}>{r.nombreGescom || 'ver ficha'}</CrmFarmacia>) },
  ];

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <Text type="secondary">Filtrar:</Text>
        <FilterBtn active={filtro === ''} onClick={() => setFiltro('')}>Todas ({rows.length})</FilterBtn>
        {SUBTIPOS.map((s) => (
          <FilterBtn key={s} active={filtro === s} onClick={() => setFiltro(s)}>
            {SUBTIPO_META[s].label} {counts[s]}
          </FilterBtn>
        ))}
      </Space>
      <Table rowKey="idFarmacia" size="small" columns={columns} dataSource={data} scroll={{ x: true }} pagination={{ pageSize: 50, showSizeChanger: true }} />
    </>
  );
};

const Inc09Table: React.FC<{ rows: Inc09Row[] }> = ({ rows }) => {
  const columns: ColumnsType<Inc09Row> = [
    { title: 'ID', dataIndex: 'idEntidad', align: 'right', sorter: (a, b) => num(a.idEntidad, b.idEntidad) },
    {
      title: 'Farmacia (Gescom-AQ)',
      dataIndex: 'nombre',
      sorter: (a, b) => txt(a.nombre, b.nombre),
      render: (_, r) => <CrmFarmacia id={r.idEntidad}>{r.nombre}</CrmFarmacia>,
    },
    {
      title: 'Motivo',
      key: 'motivo',
      render: (_, r) => {
        const m: string[] = [];
        if (r.familyClass !== 15) m.push(`FC=${r.familyClass}`);
        if (r.fBaja) m.push(`Baja=${r.fBaja}`);
        return m.join(', ');
      },
    },
    { title: 'Vinculada CU2', key: 'vinc', render: (_, r) => (r.idFarmaciaCU2 ? <Tag color="green">Vinculada</Tag> : null) },
    { title: 'id_farmacia_CU2', dataIndex: 'idFarmaciaCU2', align: 'right' },
    { title: 'Titular activo', key: 'tit', render: (_, r) => (r.tieneTitularActivo ? <Tag color="blue">Con titular</Tag> : null) },
  ];
  return <Table rowKey="idEntidad" size="small" columns={columns} dataSource={rows} scroll={{ x: true }} pagination={{ pageSize: 50, showSizeChanger: true }} />;
};

// ── Componente principal ────────────────────────────────────────────
const VinculacionIncoherenciasTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<PanelIncoherencias | null>(null);
  const [sel, setSel] = useState('far_sin_titular_gs');

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      setData(await getPanelVinculacionIncoherencias());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
        <Paragraph type="secondary" style={{ marginTop: 12 }}>
          Calculando incoherencias (incluye análisis de nombres)…
        </Paragraph>
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert type="error" showIcon message="No se pudieron cargar las incoherencias" action={<Button onClick={load}>Reintentar</Button>} />
    );
  }

  const counts: Record<string, number> = {
    far_sin_titular_gs: data.inc05.length,
    far_sin_titular_cu2: data.inc06.length,
    far_no_biunivoca: data.inc08.length,
    far_baja_fc: data.inc09.length,
    fco_titular_empleado: data.inc02.length,
    fco_no_biunivoca: data.inc07.length,
    col_repetida: data.inc04.length,
    fco_baja_fc: data.inc01.length,
  };

  const renderTabla = () => {
    switch (sel) {
      case 'far_sin_titular_gs': return <Inc05Table rows={data.inc05} />;
      case 'far_sin_titular_cu2': return <Inc06Table rows={data.inc06} />;
      case 'far_no_biunivoca': return <Inc08Table rows={data.inc08} />;
      case 'far_baja_fc': return <Inc09Table rows={data.inc09} />;
      case 'fco_titular_empleado': return <Inc02Table rows={data.inc02} />;
      case 'fco_no_biunivoca': return <Inc07Table rows={data.inc07} />;
      case 'col_repetida': return <Inc04Table rows={data.inc04} />;
      case 'fco_baja_fc': return <Inc01Table rows={data.inc01} />;
      default: return null;
    }
  };

  const meta = LISTADOS.find((l) => l.key === sel) ?? LISTADOS[0];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={load}>Actualizar</Button>
      </div>
      <Row gutter={16}>
        <Col xs={24} md={7} lg={6}>
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            {LISTADOS.map((l) => {
              const activo = l.key === sel;
              const c = counts[l.key];
              return (
                <Card
                  key={l.key}
                  size="small"
                  hoverable
                  onClick={() => setSel(l.key)}
                  styles={{ body: { padding: 12 } }}
                  style={activo ? { borderColor: '#1a3353', borderWidth: 2 } : undefined}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#37474f' }}>{l.titulo}</div>
                    <Badge count={c} showZero color={c === 0 ? '#4CAF50' : '#e65100'} overflowCount={99999} />
                  </div>
                </Card>
              );
            })}
          </Space>
        </Col>
        <Col xs={24} md={17} lg={18}>
          <Card size="small">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 24,
                flexWrap: 'wrap',
                marginBottom: 12,
              }}
            >
              <Title level={5} style={{ margin: 0 }}>
                {meta.titulo}
              </Title>
              <Text type="secondary" style={{ fontSize: 13, textAlign: 'right', flex: 1, minWidth: 240 }}>
                {meta.descripcion}
              </Text>
            </div>
            {renderTabla()}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default VinculacionIncoherenciasTab;
